import { useEffect, useRef, useState } from "react";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { uiNotification } from "../../../services/UINotificationService";
import { http } from "../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../consts/api";
import { extractApiError } from "../../../utilities/api";
import { keepSessionActive } from "../../InactivityModal/utilities";
import {
  RECORDING_KEEP_ALIVE_INTERVAL,
  RECORDING_MAX_DURATION,
} from "../../InactivityModal/config";

export const SUBSCRIPTION_ERROR = {
  limitReached: "chart_smart_limit_reached",
  planNotFound: "chart_smart_plan_not_found",
};

export function useDictation({
  text,
  onReadText,
  setProcedureNotes,
  transcription,
  onSaveNote,
}) {
  const { tCommon } = useAppTranslation.Common();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isChartSmartOpen, setIsChartSmartOpen] = useState(false);
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [isLimitReached, setIsLimitReached] = useState(false);
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      const recorder = mediaRecorderRef.current;
      if (!recorder) return;

      recorder.onstop = null;
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } finally {
        recorder.stream.getTracks().forEach((track) => track.stop());
      }
    },
    [],
  );

  const transcribeAudio = async (audioBlob) => {
    const formData = new FormData();
    formData.append("file", audioBlob, "audio.webm");

    const response = await http.post(
      HTTP_ENDPOINTS.openai.transcribe(),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data.text;
  };

  const formatNote = async (transcriptionText, templateId, customPrompt) => {
    const response = await http.post(HTTP_ENDPOINTS.openai.formatNote(), {
      text: transcriptionText,
      noteType: templateId,
      customInstructions: customPrompt,
    });

    return response.data.formattedText;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);

        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        setIsTranscribing(true);
        try {
          const transcriptionResult = await transcribeAudio(audioBlob);
          if (!isMountedRef.current) return;
          onReadText(
            textRef.current
              ? `${textRef.current}\n${transcriptionResult}`
              : transcriptionResult,
          );
        } catch (error) {
          uiNotification.error(
            error.message || tCommon("dictation.error.transcriptionFailed"),
          );
        } finally {
          if (isMountedRef.current) setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      if (
        error.message.includes("Permission denied") ||
        error.name === "NotAllowedError"
      ) {
        uiNotification.error(tCommon("dictation.error.permissionDenied"));
      }
    }
  };

  const onToggle = async () => {
    if (isRecording) {
      const recorder = mediaRecorderRef.current;

      try {
        if (recorder?.state !== "inactive") recorder?.stop();
      } finally {
        recorder?.stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
      }
    } else {
      await startRecording();
    }
  };

  useEffect(() => {
    if (!isRecording) return;

    const intervalId = setInterval(
      keepSessionActive,
      RECORDING_KEEP_ALIVE_INTERVAL,
    );
    const timeoutId = setTimeout(() => {
      onToggle();
      setIsLimitReached(true);
    }, RECORDING_MAX_DURATION);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isRecording]);

  const onStartNewRecording = () => {
    setIsLimitReached(false);
    onToggle();
  };

  const onChartSmartClick = () => {
    if (!text) {
      uiNotification.error(tCommon("dictation.error.pleaseRecordFirst"));
      return;
    }
    setIsChartSmartOpen(true);
  };

  const onTemplateSubmit = async (templateData) => {
    const { selectedTemplateId, customPrompt, saveAsProcedure } = templateData;
    setIsGeneratingNote(true);

    try {
      const formattedText = await formatNote(
        text,
        selectedTemplateId,
        customPrompt,
      );

      if (saveAsProcedure && text !== transcription) {
        setProcedureNotes(text);
      }

      setIsChartSmartOpen(false);

      if (textRef.current && saveAsProcedure && onSaveNote) {
        onSaveNote();
      }

      onReadText(formattedText);
    } catch (error) {
      const apiMessage = extractApiError(error);

      if (
        apiMessage === SUBSCRIPTION_ERROR.limitReached ||
        apiMessage === SUBSCRIPTION_ERROR.planNotFound
      ) {
        return setSubscriptionError(apiMessage);
      }

      uiNotification.error(
        error.message || tCommon("dictation.error.noteFormattingFailed"),
      );
      setIsChartSmartOpen(false);
    } finally {
      setIsGeneratingNote(false);
    }
  };

  return {
    isRecording,
    isTranscribing,
    isChartSmartOpen,
    isGeneratingNote,
    isLimitReached,
    setIsLimitReached,
    onStartNewRecording,
    onToggle,
    onChartSmartClick,
    onTemplateSubmit,
    setIsChartSmartOpen,
    subscriptionError,
    resetSubscriptionError: () => setSubscriptionError(""),
  };
}
